import { type FC, useState } from "react";

import {
  Checkbox,
  CheckboxGroup,
  Code,
  Column,
  ConfirmationDialog,
  Row,
  SectionHeading,
  Text,
} from "@hightouch/htui";

import { PermissionedButton } from "src/components/permission";
import type { ResourcePermissionInput } from "src/components/permission/use-resource-permission";

export type OrphanedConfigKeysSectionProps = {
  orphanedKeys: string[];
  config: Record<string, unknown>;
  onClear: (keys: string[]) => void;
  isDisabled?: boolean;
  permission:
    | ResourcePermissionInput<"model">
    | ResourcePermissionInput<"sync">;
};

function formatValue(value: unknown): string {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    return String(value);
  }
  return serialized.length > 120 ? `${serialized.slice(0, 117)}…` : serialized;
}

export const OrphanedConfigKeysSection: FC<OrphanedConfigKeysSectionProps> = ({
  orphanedKeys,
  config,
  onClear,
  isDisabled,
  permission,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Derive from the live key list so keys that disappear drop out of the selection without effect-based state syncing.
  const selected = orphanedKeys.filter((key) => selectedKeys.has(key));

  const toggleKey = (key: string, checked: boolean) => {
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  return (
    <Column
      gap={3}
      p={4}
      borderWidth="1px"
      borderColor="base.border"
      borderRadius="md"
      bg="base.lightBackground"
    >
      <Column gap={1}>
        <SectionHeading>Unrecognized configuration</SectionHeading>
        <Text color="text.secondary" size="sm">
          These keys are saved in this sync&apos;s configuration but don&apos;t
          match any field in the destination&apos;s current form. Most syncs
          don&apos;t need any changes here. If you&apos;re not sure what a key
          does, leave it in place.
        </Text>
      </Column>

      <CheckboxGroup>
        {orphanedKeys.map((key) => (
          <Checkbox
            key={key}
            label={key}
            badge={<Code>{formatValue(config[key])}</Code>}
            isChecked={selectedKeys.has(key)}
            isDisabled={isDisabled}
            onChange={(event) => toggleKey(key, event.target.checked)}
          />
        ))}
      </CheckboxGroup>

      <Row>
        <PermissionedButton
          variant="warning"
          isDisabled={isDisabled || selected.length === 0}
          permission={permission}
          onClick={() => setIsConfirming(true)}
        >
          Remove selected keys
        </PermissionedButton>
      </Row>

      <ConfirmationDialog
        isOpen={isConfirming}
        title="Remove configuration keys"
        confirmButtonText="Remove"
        variant="danger"
        onClose={() => setIsConfirming(false)}
        onConfirm={() => {
          onClear(selected);
          setSelectedKeys(new Set());
          setIsConfirming(false);
        }}
      >
        <Column gap={3}>
          <Text>
            This will remove the following{" "}
            {selected.length === 1 ? "key" : "keys"} when you save the sync. If
            a destination still reads a key, removing it can change how the sync
            runs.
          </Text>
          <Column as="ul" gap={1} pl={4} sx={{ listStyleType: "disc" }}>
            {selected.map((key) => (
              <Text as="li" key={key} fontWeight="medium">
                {key}
              </Text>
            ))}
          </Column>
        </Column>
      </ConfirmationDialog>
    </Column>
  );
};
